import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, MenuItem, TextField } from '@mui/material';

import Spinner from '~/components/Common/Spinner';
import { BundleItem, BundleItemType } from '~/components/Cruises/pages/Bundles/types';

import { useCreateBundleItem } from '~/hooks/apis/cruises/useBundleItemMutations';
import useAppNavigation from '~/hooks/useAppNavigation';

const ITEM_TYPES: Array<{ value: BundleItemType; label: string }> = [
  { value: 'PRE_ACCOMMODATION', label: 'Pre Accommodation' },
  { value: 'POST_ACCOMMODATION', label: 'Post Accommodation' },
];

type FormValues = {
  itemType: BundleItemType;
  displayName: string;
  maxPrice: number;
};

const INITIAL_FORM_STATE: FormValues = {
  itemType: 'PRE_ACCOMMODATION',
  displayName: '',
  maxPrice: 0,
};

type Props = {
  bundleId: string;
  bundleItem?: BundleItem;
};

function BundleItemForm({ bundleId, bundleItem }: Props) {
  const { push } = useAppNavigation();
  const [formValues, setFormValues] = useState<FormValues>(INITIAL_FORM_STATE);
  const { enqueueSnackbar } = useSnackbar();

  const createMutation = useCreateBundleItem();

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: value ?? undefined }));
    }
  }, []);

  const handleChangeNumberField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, valueAsNumber } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: isNaN(valueAsNumber) ? '' : valueAsNumber }));
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    createMutation.mutate(
      { bundleId, data: formValues },
      {
        onSuccess: () => {
          enqueueSnackbar('Bundle item saved successfully', { variant: 'success' });
          push(`/cruises/bundles/edit/${bundleId}/items`);
        },
        onError: () => {
          enqueueSnackbar('Error saving bundle item', { variant: 'error' });
        },
      },
    );
  }, [formValues, push, enqueueSnackbar, bundleId, createMutation]);

  const handleCancel = useCallback(() => {
    push(`/cruises/bundles/edit/${bundleId}/items`);
  }, [push, bundleId]);

  const isSaveEnabled = useMemo(() => {
    return (
      !createMutation.isPending &&
      formValues.itemType &&
      formValues.displayName.trim().length > 0 &&
      typeof formValues.maxPrice === 'number' &&
      formValues.maxPrice > 0
    );
  }, [formValues, createMutation.isPending]);

  useEffect(() => {
    if (bundleItem) {
      setFormValues({
        itemType: bundleItem.itemType,
        displayName: bundleItem.displayName,
        maxPrice: bundleItem.maxPrice,
      });
    }
  }, [bundleItem]);

  return (
    <Box>
      <Box mt={2}>
        <TextField
          select
          fullWidth
          name="itemType"
          label="Item Type"
          value={formValues.itemType}
          onChange={handleChangeField}
        >
          {ITEM_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box mt={2}>
        <TextField
          required
          fullWidth
          name="displayName"
          label="Display Name"
          onChange={handleChangeField}
          inputProps={{ maxLength: 100 }}
          value={formValues.displayName}
          helperText="e.g. Pre-Cruise Hotel Stay"
        />
      </Box>

      <Box mt={2}>
        <TextField
          required
          fullWidth
          name="maxPrice"
          label="Max Price (AUD)"
          type="number"
          onChange={handleChangeNumberField}
          value={formValues.maxPrice}
          helperText="Maximum hotel cost allowed for auto-booking"
          inputProps={{ min: 0, step: 1 }}
        />
      </Box>

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {createMutation.isPending && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default BundleItemForm;
